import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  SOCKET_NAMESPACES,
  SessionStatus,
  TERMINAL_SESSION_STATUSES,
  type Ack,
  type KioskClientToServer,
  type KioskConfigDTO,
  type KioskServerToClient,
  type KioskSessionView,
  type KioskSocketAuth,
  type KioskUnavailableReason,
} from '@print-saas/shared';

type KioskSocket = Socket<KioskServerToClient, KioskClientToServer>;

export type ConnectionState =
  /** First connection attempt since boot / re-pair. */
  | 'connecting'
  | 'connected'
  /** Was connected (or tried), currently retrying. */
  | 'reconnecting'
  /** Server rejected our kiosk key. */
  | 'auth-error';

export interface Availability {
  available: boolean;
  reason: KioskUnavailableReason | null;
}

export interface KioskSocketState {
  connection: ConnectionState;
  /** Raw server message for an auth rejection (for the technician). */
  authErrorMessage: string | null;
  config: KioskConfigDTO | null;
  availability: Availability | null;
  session: KioskSessionView | null;
  /** True once we've received at least one session (ack or push) on this connection. */
  sessionLoaded: boolean;
  /** Epoch ms when we lost the connection; null while connected. */
  disconnectedSince: number | null;
}

const ACK_TIMEOUT_MS = 10_000;
/** After this long without a connection the page reloads (recovers from wedged browsers / deploys). */
const RELOAD_AFTER_DISCONNECT_MS = 5 * 60_000;
/** Grace after `expiresAt` before we ask the server for a fresh QR ourselves. */
const EXPIRY_GRACE_MS = 5_000;
/** If the server never pushes the next session after a terminal state, ask for it. */
const TERMINAL_FALLBACK_MS: Partial<Record<SessionStatus, number>> = {
  COMPLETED: 25_000,
  FAILED: 45_000,
  EXPIRED: 10_000,
  CANCELLED: 10_000,
};
/** The server sweeps expired sessions every 15s; past a deadline by more than this, ask for the current one. */
const DEADLINE_GRACE_MS = 20_000;
const DEADLINE_RETRY_MS = 15_000;

const AUTH_ERROR_PATTERN = /key|auth|unauthori[sz]ed|forbidden|invalid|not found|revoked/i;

const initialState: KioskSocketState = {
  connection: 'connecting',
  authErrorMessage: null,
  config: null,
  availability: null,
  session: null,
  sessionLoaded: false,
  disconnectedSince: Date.now(),
};

/**
 * Owns the `/kiosk` Socket.IO connection for the display and exposes the latest
 * config / availability / session. Also runs the QR-expiry and terminal-state watchdogs.
 */
export function useKioskSocket(
  apiUrl: string,
  auth: KioskSocketAuth,
): KioskSocketState & {
  selectPrinter: (name: string, pin?: string) => Promise<void>;
  verifyPin: (pin: string) => Promise<void>;
} {
  const [state, setState] = useState<KioskSocketState>(initialState);
  const socketRef = useRef<KioskSocket | null>(null);
  /** Bumped on every server push so a slower ack can't overwrite a newer session. */
  const pushSeq = useRef(0);
  const lastRefreshAt = useRef(0);

  const patch = useCallback((p: Partial<KioskSocketState>) => {
    setState((s) => ({ ...s, ...p }));
  }, []);

  const applySessionAck = useCallback(
    (seqAtRequest: number) => (err: Error | null, res: Ack<KioskSessionView | null> | undefined) => {
      if (err || !res) {
        console.warn('[kiosk] session request timed out', err);
        return;
      }
      if (!res.ok) {
        console.warn('[kiosk] session request failed:', res.error);
        return;
      }
      if (pushSeq.current !== seqAtRequest) return; // a newer push already arrived
      patch({ session: res.data, sessionLoaded: true });
    },
    [patch],
  );

  const requestCurrent = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.timeout(ACK_TIMEOUT_MS).emit('session:current', applySessionAck(pushSeq.current));
  }, [applySessionAck]);

  const requestRefresh = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    lastRefreshAt.current = Date.now();
    socket.timeout(ACK_TIMEOUT_MS).emit('session:refresh', applySessionAck(pushSeq.current));
  }, [applySessionAck]);

  // --- connection lifecycle ------------------------------------------------
  useEffect(() => {
    setState({ ...initialState, disconnectedSince: Date.now() });

    const socket: KioskSocket = io(`${apiUrl}${SOCKET_NAMESPACES.kiosk}`, {
      auth,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1_000,
      reconnectionDelayMax: 10_000,
      timeout: 15_000,
    });
    socketRef.current = socket;
    let retryTimer: number | undefined;

    const scheduleManualReconnect = (ms: number) => {
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => {
        if (!socket.connected) socket.connect();
      }, ms);
    };

    socket.on('connect', () => {
      window.clearTimeout(retryTimer);
      patch({ connection: 'connected', authErrorMessage: null, disconnectedSince: null });
      socket.timeout(ACK_TIMEOUT_MS).emit('session:current', applySessionAck(pushSeq.current));
    });

    socket.on('disconnect', (reason) => {
      setState((s) => ({
        ...s,
        connection: s.connection === 'auth-error' ? s.connection : 'reconnecting',
        disconnectedSince: s.disconnectedSince ?? Date.now(),
      }));
      // A server-side disconnect doesn't auto-reconnect; do it ourselves after a pause.
      if (reason === 'io server disconnect') scheduleManualReconnect(3_000);
    });

    socket.on('connect_error', (err) => {
      if (!socket.active) {
        // Rejected by the server's middleware; the client will not retry on its own.
        if (AUTH_ERROR_PATTERN.test(err.message)) {
          patch({ connection: 'auth-error', authErrorMessage: err.message });
          scheduleManualReconnect(60_000); // in case the owner fixes it server-side
        } else {
          setState((s) => (s.connection === 'connecting' ? s : { ...s, connection: 'reconnecting' }));
          scheduleManualReconnect(10_000);
        }
        return;
      }
      setState((s) => (s.connection === 'connected' ? { ...s, connection: 'reconnecting' } : s));
    });

    socket.on('kiosk:config', (config) => patch({ config }));
    socket.on('kiosk:availability', (availability) => patch({ availability }));
    socket.on('session:update', (session) => {
      pushSeq.current += 1;
      // A late update for an older, already-ended session must not replace the current QR.
      setState((s) =>
        session && s.session && session.id !== s.session.id && TERMINAL_SESSION_STATUSES.includes(session.status)
          ? s
          : { ...s, session, sessionLoaded: true },
      );
    });

    return () => {
      window.clearTimeout(retryTimer);
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [apiUrl, auth, patch, applySessionAck]);

  // --- reload if we've been offline for too long ---------------------------
  useEffect(() => {
    if (state.disconnectedSince === null) return;
    const remaining = state.disconnectedSince + RELOAD_AFTER_DISCONNECT_MS - Date.now();
    const t = window.setTimeout(() => window.location.reload(), Math.max(remaining, 1_000));
    return () => window.clearTimeout(t);
  }, [state.disconnectedSince]);

  // --- QR expiry watchdog --------------------------------------------------
  const connected = state.connection === 'connected';
  const sessionId = state.session?.id ?? null;
  const sessionStatus = state.session?.status ?? null;
  const sessionExpiresAt = state.session?.expiresAt ?? null;
  useEffect(() => {
    if (!connected || !sessionId || sessionStatus !== SessionStatus.WAITING || !sessionExpiresAt) {
      return;
    }
    const expiresAt = Date.parse(sessionExpiresAt);
    if (Number.isNaN(expiresAt)) return;
    // If our clock disagrees badly with the server's, don't hammer it with refreshes.
    const minDelay = Date.now() - lastRefreshAt.current < 60_000 ? 30_000 : EXPIRY_GRACE_MS;
    const delay = Math.max(expiresAt + EXPIRY_GRACE_MS - Date.now(), minDelay);
    const t = window.setTimeout(requestRefresh, delay);
    return () => window.clearTimeout(t);
  }, [connected, sessionId, sessionStatus, sessionExpiresAt, requestRefresh]);

  // --- terminal-state watchdog ---------------------------------------------
  // Never earlier than the owner's "Collect your prints" time, or it would cut that screen short.
  const collectMs = (state.config?.collectPrintsSeconds ?? 0) * 1000;
  useEffect(() => {
    if (!connected || !sessionStatus) return;
    const base = TERMINAL_FALLBACK_MS[sessionStatus];
    if (base === undefined) return;
    const rotatesAfterCollect = sessionStatus === SessionStatus.COMPLETED || sessionStatus === SessionStatus.FAILED;
    const ms = rotatesAfterCollect ? Math.max(base, collectMs + DEADLINE_GRACE_MS) : base;
    const t = window.setTimeout(requestCurrent, ms);
    return () => window.clearTimeout(t);
  }, [connected, sessionId, sessionStatus, collectMs, requestCurrent]);

  // --- deadline watchdog -----------------------------------------------------
  // An ACTIVE / AWAITING_PAYMENT session ends server-side at its deadline. If that update is
  // missed (network blip, API restart), don't sit on a 0:00 countdown: ask what to show.
  const leftDeadline = state.session?.customerLeftDeadline ?? state.session?.customerConnectingDeadline ?? null;
  useEffect(() => {
    if (!connected || !sessionExpiresAt) return;
    if (sessionStatus !== SessionStatus.ACTIVE && sessionStatus !== SessionStatus.AWAITING_PAYMENT) return;
    const deadline = Math.min(Date.parse(sessionExpiresAt), leftDeadline ? Date.parse(leftDeadline) : Infinity);
    if (Number.isNaN(deadline)) return;
    let retry: number | undefined;
    const t = window.setTimeout(() => {
      requestCurrent();
      retry = window.setInterval(requestCurrent, DEADLINE_RETRY_MS);
    }, Math.max(deadline + DEADLINE_GRACE_MS - Date.now(), 0));
    return () => {
      window.clearTimeout(t);
      window.clearInterval(retry);
    };
  }, [connected, sessionId, sessionStatus, sessionExpiresAt, leftDeadline, requestCurrent]);

  const unavailable = state.availability?.available === false;
  // --- session-less watchdog (e.g. ack lost) ---------------------------------
  useEffect(() => {
    if (!connected || sessionId) return;
    if (unavailable) return; // a null session is expected while unavailable
    const t = window.setInterval(requestCurrent, 15_000);
    return () => window.clearInterval(t);
  }, [connected, sessionId, unavailable, requestCurrent]);

  const connectedSocket = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) throw new Error('Not connected to the server');
    return socket.timeout(ACK_TIMEOUT_MS);
  }, []);

  /** First-run printer choice; the agent switches as soon as the server pushes the new config. */
  const selectPrinter = useCallback(
    async (name: string, pin?: string) => {
      const res = (await connectedSocket().emitWithAck('printer:select', { name, pin })) as Ack<null>;
      if (!res.ok) throw new Error(res.error);
    },
    [connectedSocket],
  );

  /** Checks the owner's kiosk PIN before a technician action. */
  const verifyPin = useCallback(
    async (pin: string) => {
      const res = (await connectedSocket().emitWithAck('setup:verify-pin', pin)) as Ack<null>;
      if (!res.ok) throw new Error(res.error);
    },
    [connectedSocket],
  );

  return { ...state, selectPrinter, verifyPin };
}
